import { goAnchor } from "./app-nav";
import { t } from "@/lib/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import AgentPanel from "./aiagents-panel";
import {
  agentCapabilities,
  agentChannels,
  agentHints,
  agentIntro,
  agentPreviewChat,
  agentPreviewContact,
  agentTemplates,
  agentTemplatesSubtitle,
  agentTemplatesTitle,
  demoAgents,
  type AgentRecord,
  type AgentTemplate,
} from "@/mocks/aiagents";

/* Живой макет окна «Агенты искусственного интеллекта»: приветственный экран с возможностями,
   галерея шаблонов и список созданных агентов. Включите демо-данные — и в рабочем пространстве
   появятся три готовых агента с цифрами. */

const RAIL = [
  { id: "ai-agents", icon: "ri-robot-2-line", label: t("Агенты ИИ"), anchor: "#ai-agents", active: true },
  { id: "inbox", icon: "ri-inbox-line", label: t("Входящие"), anchor: "#inbox-app", active: false },
  { id: "contacts", icon: "ri-contacts-book-2-line", label: t("Контакты"), anchor: "#contacts-app", active: false },
  { id: "dashboard", icon: "ri-dashboard-line", label: t("Дашборд"), anchor: "#dashboard", active: false },
  { id: "channels", icon: "ri-share-forward-line", label: t("Каналы"), anchor: "#channels", active: false },
  { id: "ai-assist", icon: "ri-sparkling-line", label: "AI Assist", anchor: "#ai-assist", active: false },
  { id: "lifecycle", icon: "ri-refresh-line", label: t("Жизненный цикл"), anchor: "#lifecycle", active: false },
  { id: "broadcasts", icon: "ri-mail-send-line", label: t("Рассылки"), anchor: "#broadcasts", active: false },
  { id: "workflows", icon: "ri-flow-chart", label: t("Рабочие процессы"), anchor: "#workflows", active: false },
  { id: "reports", icon: "ri-bar-chart-2-line", label: t("Отчеты"), anchor: "#reports", active: false },
  { id: "settings", icon: "ri-settings-3-line", label: t("Настройки"), anchor: "#settings", active: false },
];

const channelLabel = (id: string) => agentChannels.find((c) => c.id === id)?.label ?? id;
const channelIcon = (id: string) => agentChannels.find((c) => c.id === id)?.icon ?? "ri-message-3-line";

/** превью диалога + карточка контакта — иллюстрация приветственного экрана */
function IntroArt() {
  return (
    <div className="relative overflow-hidden rounded-btn border border-border bg-background/60 p-2.5">
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_8.75rem]">
        <div className="min-w-0">
          <p className="mx-auto w-fit rounded-full border border-border bg-card px-2 py-0.5 text-[9px] font-semibold text-muted-foreground">{t("Сегодня")}</p>
          <div className="mt-2 flex flex-col gap-1.5">
            {agentPreviewChat.map((m) => (
              <p key={m.id} className="max-w-[94%] rounded-btn rounded-ss-[3px] border border-border/70 bg-card px-2 py-1.5 text-[9.5px] leading-relaxed text-foreground/85">
                {m.text}
              </p>
            ))}
            <div className="max-w-[96%] self-end rounded-btn rounded-se-[3px] border border-primary/25 bg-primary/[.08] px-2 py-1.5">
              <p className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-wide text-primary">
                <i className="ri-robot-2-line text-[9px] leading-none" aria-hidden />
                AI Agent
              </p>
              <p className="mt-0.5 text-[9.5px] leading-relaxed text-foreground/85">{t("Конечно, курс включает живые занятия дважды в неделю.")}</p>
            </div>
          </div>
        </div>

        <div className="min-w-0 rounded-btn border border-border bg-card p-2">
          <p className="flex items-center gap-1 text-[9px] font-semibold text-foreground/80">
            <i className="ri-window-line text-[10px] leading-none text-muted-foreground" aria-hidden />
            {agentPreviewContact.heading}
          </p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <span className="grid size-6 flex-none place-items-center rounded-full bg-primary/15 text-[8.5px] font-bold text-primary" aria-hidden>
              {agentPreviewContact.initials}
            </span>
            <div className="min-w-0">
              <p className="truncate text-[9.5px] font-semibold leading-tight">{agentPreviewContact.name}</p>
              <p className="truncate text-[8.5px] text-muted-foreground">ID: {agentPreviewContact.id}</p>
            </div>
          </div>
          <p className="mt-2 text-[8.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t("Поля контакта")}</p>
          <div className="mt-1 flex flex-col gap-1.5">
            {agentPreviewContact.fields.map((f) => (
              <div key={f.label}>
                <p className="text-[8.5px] text-muted-foreground">{f.label}</p>
                <p className="truncate text-[9.5px] font-medium text-primary">{f.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="mt-2 flex items-center gap-1 text-[9px] text-muted-foreground">
        <i className="ri-sparkling-line text-[10px] leading-none text-amber-500" aria-hidden />
        {t("Агент отвечает сам и сам заполняет карточку клиента")}
      </p>
    </div>
  );
}

function AiAgentsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [view, setView] = useState<"intro" | "templates" | "agent">("intro");
  const [selected, setSelected] = useState<string | null>(null);
  const [demo, setDemo] = useState(false);
  const [created, setCreated] = useState<AgentRecord[]>([]);
  const [panel, setPanel] = useState<null | { mode: "new" | "edit"; template?: AgentTemplate; agent?: AgentRecord }>(null);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  };

  const all = useMemo(() => {
    const base = demo ? demoAgents : [];
    const overrides = new Map(created.map((a) => [a.id, a]));
    const merged = base.map((a) => overrides.get(a.id) ?? a);
    const extra = created.filter((a) => !base.some((b) => b.id === a.id));
    return [...merged, ...extra];
  }, [demo, created]);

  const current = all.find((a) => a.id === selected) ?? null;
  const activeCount = all.filter((a) => a.enabled).length;
  const totalDialogs = all.reduce((sum, a) => sum + a.dialogs, 0);
  const totalResolved = all.reduce((sum, a) => sum + a.resolved, 0);

  const jump = (anchor: string, label: string) => {
    if (goAnchor(anchor)) return;
    flash(t("Раздел «{label}» в демо не открывается", { label }));
  };

  const save = (record: AgentRecord) => {
    const isNew = !all.some((a) => a.id === record.id);
    setCreated((prev) => (prev.some((a) => a.id === record.id) ? prev.map((a) => (a.id === record.id ? record : a)) : [...prev, record]));
    setPanel(null);
    setSelected(record.id);
    setView("agent");
    flash(isNew ? t("Агент «{name}» создан и включён — он готов принимать диалоги", { name: record.name }) : t("Настройки агента «{name}» сохранены", { name: record.name }));
  };

  const toggleAgent = (id: string) => {
    const agent = all.find((a) => a.id === id);
    if (!agent) return;
    setCreated((prev) => {
      const next = { ...agent, enabled: !agent.enabled };
      return prev.some((a) => a.id === id) ? prev.map((a) => (a.id === id ? next : a)) : [...prev, next];
    });
    flash(agent.enabled ? t("Агент «{name}» выключен и больше не отвечает", { name: agent.name }) : t("Агент «{name}» включён и снова принимает диалоги", { name: agent.name }));
  };

  const toggleDemo = () => {
    const next = !demo;
    setDemo(next);
    if (next) {
      setSelected(demoAgents[0].id);
      setView("agent");
      flash(t("Демо-данные включены: три агента уже работают с диалогами"));
    } else {
      setCreated((prev) => prev.filter((a) => !demoAgents.some((d) => d.id === a.id)));
      setSelected(null);
      setView("intro");
      flash(t("Демо-данные выключены — в рабочем пространстве снова нет агентов"));
    }
  };

  const openTemplates = () => {
    setView("templates");
    setSelected(null);
  };

  return (
    <Band id="ai-agents" tone="tint">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">{str(brand) || "Hub"} / {t("рабочее пространство → агенты ИИ")}</span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className={cn("size-1.5 rounded-full", activeCount > 0 ? "bg-emerald-500" : "bg-foreground/30")} aria-hidden />
            {t("Активных агентов: {n}", { n: activeCount })}
          </span>
        </div>

        {/* пробный период */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-primary/[.08] px-3.5 py-2 text-[11.5px]">
          <i className="ri-information-line text-[13px] leading-none text-primary" aria-hidden />
          <span className="min-w-0 flex-1 leading-relaxed text-foreground/80">
            {t("Пробный период Growth Plan закончится 3 ноября 2026 — через 6 дней. Обновите тариф, чтобы не потерять доступ.")}
          </span>
          <button
            type="button"
            onClick={() => jump("#onboarding", t("Начало работы"))}
            className="inline-flex h-6 flex-none cursor-pointer items-center whitespace-nowrap rounded-full border border-primary/30 px-2.5 text-[10.5px] font-semibold text-primary transition hover:bg-primary/10"
          >
            {t("Чеклист онбординга")}
          </button>
          <button
            type="button"
            onClick={() => flash(t("Демо-режим: смена тарифа здесь недоступна"))}
            className="inline-flex h-6 flex-none cursor-pointer items-center whitespace-nowrap rounded-full bg-primary px-2.5 text-[10.5px] font-semibold text-primary-foreground transition hover:brightness-110"
          >
            {t("Обновить тариф")}
          </button>
        </div>

        <div className="flex">
          {/* иконная панель */}
          <div className="hidden w-12 flex-none flex-col items-center gap-1 border-e border-border py-3 lg:flex">
            {RAIL.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => jump(r.anchor, r.label)}
                title={r.label}
                aria-label={r.label}
                className={cn(
                  "grid size-8 cursor-pointer place-items-center rounded-btn transition",
                  r.active ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <i className={cn(r.icon, "text-[15px] leading-none")} aria-hidden />
              </button>
            ))}
            <span className="mt-auto grid size-8 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground" aria-hidden>
              {t("В")}
            </span>
          </div>

          {/* список агентов */}
          <div className="hidden w-[15.5rem] flex-none flex-col border-e border-border lg:flex">
            <div className="flex items-center gap-1 px-3 pb-1.5 pt-3">
              <p className="text-[13px] font-semibold">{t("Агенты ИИ")}</p>
              <button
                type="button"
                onClick={() => setPanel({ mode: "new" })}
                aria-label={t("Создать AI-агента")}
                title={t("Создать AI-агента")}
                className="ms-auto grid size-6 cursor-pointer place-items-center rounded-[5px] text-muted-foreground transition hover:bg-muted hover:text-primary"
              >
                <i className="ri-add-line text-[14px] leading-none" aria-hidden />
              </button>
            </div>

            <div className="flex flex-col gap-0.5 px-2">
              <SideItem
                active={view === "intro"}
                icon="ri-sparkling-line"
                label={t("Обзор")}
                onClick={() => {
                  setView("intro");
                  setSelected(null);
                }}
              />
              <SideItem active={view === "templates"} icon="ri-layout-grid-line" label={t("Шаблоны агентов")} count={agentTemplates.length} onClick={openTemplates} />
            </div>

            <div className="mt-2 flex items-center gap-1 px-3">
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t("Мои агенты")}</span>
              <span className="text-[10px] tabular-nums text-muted-foreground/80">{all.length}</span>
            </div>

            <div className="mt-1 flex min-h-0 flex-col gap-0.5 overflow-y-auto px-2">
              {all.length === 0 ? (
                <p className="px-2 py-2 text-[10.5px] leading-relaxed text-muted-foreground">
                  {t("Пока ни одного агента. Начните с шаблона или включите демо-данные внизу колонки.")}
                </p>
              ) : (
                all.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      setSelected(a.id);
                      setView("agent");
                    }}
                    aria-current={selected === a.id ? "page" : undefined}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-[7px] text-start transition",
                      selected === a.id ? "bg-primary/15 text-primary" : "text-foreground/75 hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <span className="grid size-5 flex-none place-items-center text-[12px] leading-none" aria-hidden>
                      {a.emoji}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[11.5px] font-medium">{a.name}</span>
                      <span className="block truncate text-[9.5px] text-muted-foreground">{a.role}</span>
                    </span>
                    <span className={cn("size-1.5 flex-none rounded-full", a.enabled ? "bg-emerald-500" : "bg-foreground/25")} aria-hidden />
                  </button>
                ))
              )}
            </div>

            <div className="mt-auto border-t border-border p-2.5">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 text-[10.5px] font-semibold leading-tight text-muted-foreground">{t("Демо-данные")}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={demo}
                  aria-label={t("Демо-данные")}
                  onClick={toggleDemo}
                  className={cn("relative h-5 w-9 flex-none cursor-pointer rounded-full border transition", demo ? "border-primary bg-primary" : "border-border bg-foreground/10")}
                >
                  <span className={cn("absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-card transition-all", demo ? "start-[1.15rem]" : "start-[0.15rem]")} aria-hidden />
                </button>
              </div>
              <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground">
                {demo ? t("Три агента работают с диалогами") : t("Включите, чтобы увидеть агентов с цифрами")}
              </p>
            </div>
          </div>

          {/* основная область */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
              <div className="flex min-w-0 items-center gap-1.5">
                {view === "templates" ? (
                  <button
                    type="button"
                    onClick={() => {
                      setView(selected ? "agent" : "intro");
                    }}
                    aria-label={t("Назад")}
                    title={t("Назад")}
                    className="grid size-6 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                  >
                    <i className="ri-arrow-left-s-line text-[15px] leading-none" aria-hidden />
                  </button>
                ) : null}
                <h4 className="truncate text-[13px] font-semibold">
                  {view === "templates" ? agentTemplatesTitle : view === "agent" && current ? current.name : agentIntro.title}
                </h4>
              </div>

              <div className="ms-auto flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => flash(t("Демо-режим: подробная справка по AI-агентам в макете недоступна"))}
                  className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn border border-border px-2 text-[10.5px] font-semibold text-muted-foreground transition hover:border-primary/50 hover:text-primary"
                >
                  <i className="ri-book-open-line text-[12px] leading-none" aria-hidden />
                  {t("Узнать больше")}
                </button>
                <button
                  type="button"
                  role="switch"
                  aria-checked={demo}
                  aria-label={t("Демо-данные")}
                  onClick={toggleDemo}
                  className={cn("relative h-5 w-9 flex-none cursor-pointer rounded-full border transition lg:hidden", demo ? "border-primary bg-primary" : "border-border bg-foreground/10")}
                >
                  <span className={cn("absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-card transition-all", demo ? "start-[1.15rem]" : "start-[0.15rem]")} aria-hidden />
                </button>
              </div>
            </div>

            {/* мобильные разделы */}
            <div className="flex gap-1.5 overflow-x-auto border-b border-border px-2.5 py-2 lg:hidden">
              {[
                { id: "intro", label: t("Обзор") },
                { id: "templates", label: t("Шаблоны") },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setView(t.id as "intro" | "templates");
                    setSelected(null);
                  }}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    view === t.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground",
                  )}
                >
                  {t.label}
                </button>
              ))}
              {all.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    setSelected(a.id);
                    setView("agent");
                  }}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    selected === a.id && view === "agent" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground",
                  )}
                >
                  <span aria-hidden>{a.emoji}</span>
                  {a.name}
                </button>
              ))}
            </div>

            <div className="min-h-[26rem]">
              {view === "intro" ? (
                <div className="grid gap-4 p-3.5 md:p-5 lg:grid-cols-[1.05fr_.95fr] lg:gap-6">
                  <div className="flex min-w-0 flex-col">
                    <h5 className="text-[19px] font-semibold leading-snug md:text-[23px]">{agentIntro.heading}</h5>
                    <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">{agentIntro.lead}</p>

                    <ul className="mt-4 flex flex-col gap-2.5">
                      {agentCapabilities.map((c) => (
                        <li key={c.text} className="flex items-start gap-2.5">
                          <span className="grid size-7 flex-none place-items-center rounded-full border border-primary/25 bg-primary/[.08] text-primary" aria-hidden>
                            <i className={cn(c.icon, "text-[13px] leading-none")} />
                          </span>
                          <span className="min-w-0 text-[12px] leading-relaxed text-foreground/80">{c.text}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      onClick={openTemplates}
                      className="mt-5 inline-flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                    >
                      <i className="ri-robot-2-line text-[14px] leading-none" aria-hidden />
                      {agentIntro.cta}
                    </button>
                    <p className="mt-2 text-[10.5px] text-muted-foreground">
                      {demo ? t("В демо уже работают три агента — откройте их в левой колонке.") : t("Первый шаг — выбрать шаблон под задачу.")}
                    </p>
                  </div>

                  <div className="min-w-0">
                    <IntroArt />
                  </div>
                </div>
              ) : null}

              {view === "templates" ? (
                <div className="p-3.5 md:p-5">
                  <h5 className="text-[15px] font-semibold leading-snug">{agentTemplatesTitle}</h5>
                  <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-muted-foreground">{agentTemplatesSubtitle}</p>

                  <div className="mt-3.5 grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                    {agentTemplates.map((tpl) => (
                      <div key={tpl.id} className="flex flex-col rounded-btn border border-border bg-background/50 p-3 transition hover:border-primary/40">
                        <span className="grid size-8 place-items-center rounded-full bg-primary/[.08] text-[15px] leading-none" aria-hidden>
                          {tpl.emoji}
                        </span>
                        <h6 className="mt-2 text-[13px] font-semibold leading-snug">{tpl.name}</h6>
                        <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{tpl.description}</p>
                        <button
                          type="button"
                          onClick={() => setPanel({ mode: "new", template: tpl })}
                          className="mt-3 inline-flex h-8 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border bg-card text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
                        >
                          {t("Использовать шаблон")}
                        </button>
                      </div>
                    ))}

                    <div className="flex flex-col items-center justify-center gap-1.5 rounded-btn border border-dashed border-border bg-background/30 p-4 text-center sm:col-span-2 xl:col-span-1">
                      <h6 className="text-[13px] font-semibold leading-snug">{t("Создать с нуля")}</h6>
                      <p className="text-[11px] leading-relaxed text-muted-foreground">{t("Соберите своего AI-агента под процессы компании.")}</p>
                      <button
                        type="button"
                        onClick={() => setPanel({ mode: "new" })}
                        className="mt-1 inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[11.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                      >
                        <i className="ri-magic-line text-[13px] leading-none" aria-hidden />
                        {t("Начать с нуля")}
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => jump("#ai-knowledge", t("Источники знаний"))}
                    className="mt-3 flex w-full cursor-pointer items-start gap-2 rounded-btn border border-border bg-card/60 p-2.5 text-start transition hover:border-primary/40"
                  >
                    <i className="ri-information-line mt-0.5 text-[13px] leading-none text-primary" aria-hidden />
                    <span className="min-w-0 text-[10.5px] leading-relaxed text-muted-foreground">
                      {t("Агент отвечает по источникам знаний и шаблонам. Проверьте, что прайсы и регламенты загружены — иначе он передаст диалог человеку.")}
                    </span>
                  </button>
                </div>
              ) : null}

              {view === "agent" && current ? (
                <div className="p-3.5 md:p-5">
                  <div className="flex flex-wrap items-start justify-between gap-2.5">
                    <div className="flex min-w-0 items-start gap-2.5">
                      <span className="grid size-9 flex-none place-items-center rounded-full bg-primary/[.08] text-[16px] leading-none" aria-hidden>
                        {current.emoji}
                      </span>
                      <div className="min-w-0">
                        <h5 className="truncate text-[14.5px] font-semibold leading-snug">{current.name}</h5>
                        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <i className="ri-robot-2-line text-[11px] leading-none" aria-hidden />
                            {current.role}
                          </span>
                          <span className="text-border" aria-hidden>
                            ·
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className={cn("size-1.5 rounded-full", current.enabled ? "bg-emerald-500" : "bg-foreground/30")} aria-hidden />
                            {current.enabled ? t("включён") : t("выключен")}
                          </span>
                          {current.template !== "scratch" ? (
                            <>
                              <span className="text-border" aria-hidden>
                                ·
                              </span>
                              <span>{t("шаблон «{name}»", { name: agentTemplates.find((x) => x.id === current.template)?.name ?? current.role })}</span>
                            </>
                          ) : null}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setPanel({ mode: "edit", agent: current })}
                        className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn border border-border px-2 text-[10.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
                      >
                        <i className="ri-settings-3-line text-[12px] leading-none" aria-hidden />
                        {t("Настроить")}
                      </button>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={current.enabled}
                        aria-label={t("Включить агента")}
                        onClick={() => toggleAgent(current.id)}
                        className={cn(
                          "relative h-5 w-9 flex-none cursor-pointer rounded-full border transition",
                          current.enabled ? "border-primary bg-primary" : "border-border bg-foreground/10",
                        )}
                      >
                        <span className={cn("absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-card transition-all", current.enabled ? "start-[1.15rem]" : "start-[0.15rem]")} aria-hidden />
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    {[
                      { label: t("Диалогов"), value: current.dialogs, icon: "ri-chat-3-line" },
                      { label: t("Решено без человека"), value: current.resolved, icon: "ri-checkbox-circle-line" },
                      { label: t("Передано людям"), value: Math.max(current.dialogs - current.resolved, 0), icon: "ri-user-shared-line" },
                    ].map((s) => (
                      <div key={s.label} className="rounded-btn border border-border bg-background/50 p-2.5">
                        <p className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          <i className={cn(s.icon, "text-[11px] leading-none text-primary")} aria-hidden />
                          {s.label}
                        </p>
                        <p className="mt-1 text-[18px] font-semibold leading-none tabular-nums">{s.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-2.5 grid gap-2.5 lg:grid-cols-2">
                    <div className="rounded-btn border border-border bg-background/50 p-3">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t("Цель")}</p>
                      <p className="mt-1.5 text-[11.5px] leading-relaxed text-foreground/80">{current.goal || t("Не задана")}</p>
                      <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t("Инструкции")}</p>
                      <p className="mt-1.5 text-[11.5px] leading-relaxed text-foreground/80">{current.instructions || t("Не заданы")}</p>
                    </div>

                    <div className="rounded-btn border border-border bg-background/50 p-3">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t("Каналы и тон")}</p>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {current.channels.length === 0 ? (
                          <span className="text-[11px] text-muted-foreground">{t("Каналы не выбраны")}</span>
                        ) : (
                          current.channels.map((ch) => (
                            <span key={ch} className="inline-flex items-center gap-1 rounded-full bg-primary/[.08] px-2 py-0.5 text-[10px] font-semibold text-primary">
                              <i className={cn(channelIcon(ch), "text-[10px] leading-none")} aria-hidden />
                              {channelLabel(ch)}
                            </span>
                          ))
                        )}
                      </div>
                      <p className="mt-2 flex items-center gap-1.5 text-[11px] text-foreground/80">
                        <i className="ri-chat-voice-line text-[12px] leading-none text-primary" aria-hidden />
                        {t("Тон: {tone}", { tone: current.tone })}
                      </p>
                      <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                        <i className={cn(current.handoff ? "ri-user-shared-line" : "ri-user-forbid-line", "mt-0.5 text-[12px] leading-none text-primary")} aria-hidden />
                        {current.handoff ? t("Передаёт диалог человеку, если не уверен в ответе") : t("Отвечает сам до конца, без передачи человеку")}
                      </p>
                      <button
                        type="button"
                        onClick={() => jump("#ai-knowledge", t("Источники знаний"))}
                        className="mt-2.5 inline-flex cursor-pointer items-center gap-1 text-[10.5px] font-semibold text-primary underline decoration-dotted"
                      >
                        <i className="ri-book-open-line text-[11px] leading-none" aria-hidden />
                        {t("Источники знаний агента")}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* подсказки */}
            <div className="grid gap-2 border-t border-border p-3.5 sm:grid-cols-3">
              {agentHints.map((h) => (
                <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
                  <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                    <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                    {h.title}
                  </p>
                  <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
                </div>
              ))}
            </div>

            <p className="px-3.5 pb-3.5 text-[11px] text-muted-foreground">
              {all.length > 0
                ? t("Всего агентов: {total} · диалогов обработано: {dialogs} · решено без человека: {resolved}. Демо-режим: данные живут только на этой странице.", { total: all.length, dialogs: totalDialogs, resolved: totalResolved })
                : t("Демо-режим: агенты и цифры живут только на этой странице и не сохраняются после перезагрузки.")}
            </p>
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {panel ? <AgentPanel mode={panel.mode} template={panel.template} agent={panel.agent} onClose={() => setPanel(null)} onSave={save} /> : null}
      </div>
    </Band>
  );
}

function SideItem({ active, icon, label, count, onClick }: { active: boolean; icon: string; label: string; count?: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-[7px] text-start text-[12px] font-medium transition",
        active ? "bg-primary/15 text-primary" : "text-foreground/75 hover:bg-muted hover:text-foreground",
      )}
    >
      <span className={cn("grid size-5 flex-none place-items-center text-[13px] leading-none", active ? "text-primary" : "text-muted-foreground")} aria-hidden>
        <i className={icon} />
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {typeof count === "number" ? <span className={cn("flex-none tabular-nums text-[10.5px]", active ? "text-primary" : "text-muted-foreground/80")}>{count}</span> : null}
    </button>
  );
}

export default AiAgentsDemo;